/**
 * Contains a render crash to the page that caused it.
 *
 * Without this, one unguarded property read anywhere in a route's tree
 * unmounts the entire application and replaces it with react-router's
 * default "Unexpected Application Error!" stack trace — which is exactly
 * what happened on /help, three deploys running, when a hand-authored
 * payload was thinner than the renderer assumed.
 *
 * The right fix for each of those was to make the renderer tolerant, and
 * those fixes landed. This is the second layer: the guarantee that the NEXT
 * shape nobody anticipated degrades to one honest card with a retry button,
 * while the sidebar, the header and every other route keep working.
 *
 * Class component because error boundaries still cannot be written as
 * hooks. `resetKey` lets a caller (typically the current pathname) clear
 * the error when the user navigates away and back.
 */
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './button';

interface Props {
  children: ReactNode;
  /** Human name for the thing that failed, e.g. "the help page". */
  label?: string;
  /** When this changes the boundary forgets its error and re-renders. */
  resetKey?: string | number;
}

interface State {
  error: Error | null;
}

export class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the real stack in the console for whoever is debugging — the
    // on-screen card deliberately shows only the message.
    console.error('[PageErrorBoundary]', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const label = this.props.label ?? 'this page';
    return (
      <div
        role="alert"
        className="mx-auto mt-10 max-w-lg rounded-lg border border-destructive/30 bg-destructive/5 p-6"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="text-sm font-semibold text-foreground">
                Something went wrong loading {label}.
              </p>
              <p className="mt-1 break-words font-mono text-xs text-muted-foreground">
                {this.state.error.message}
              </p>
            </div>
            <Button size="sm" variant="outline" leftIcon={<RotateCcw />} onClick={this.reset}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
